<template>
  <div>
    <div v-if="!isLoadingTransactions" class="md:mx-14 mx-4">
      <div class="font-normal text-gray-4 my-4 text-3xl"></div>
      <div class="grid lg:gap-6 gap-y-4 lg:grid-cols-12 grid-cols-1">
        <div class="col-span-6 dashboard-card">
          <div class="flex ml-8 mt-7 pb-1.5 items-center justify-between">
            <div class="flex font-normal text-root-1 text-sm md:text-base">
              $ Balance in USD
            </div>
            <div class="mr-3 cursor-pointer"><vertical-dots-icon /></div>
          </div>
          <div
            class="flex ml-8 text-root-2 text-41px flex-col font-bold py-12 justify-center"
          >
            <!-- <ICountUp
                :delay="countup.delay"
                :endVal="newBalance"
                :options="countup.options"
              /> -->
            <div class="flex items-center text-2xl lg:text-41px">
              {{ parseMoney(digitsBeforeDecimal(totalBalance)) }}
              <div>
                {{
                  noAfterDecimal(totalBalance) > 0 &&
                  digitsAfterDecimal(totalBalance)
                    ? '.'
                    : ''
                }}
              </div>
              <div class="text-root-3">
                {{ digitsAfterDecimal(totalBalance) }}
              </div>
            </div>
            <div
              class="text-primary flex items-center text-xs md:text-sm md:mt-3 font-semibold"
            >
              <interest-up class="mr-2.5" />{{ apyValue }} % APY
            </div>
            <div class="flex text-sm mt-7 items-center">
              <div
                @click="goToDeposit()"
                class="cursor-pointer flex items-center"
              >
                <div
                  class="text-bg-root-1 bg-gray-10 border h-4 mr-2 pb-0.5 w-4 flex justify-center items-center rounded-3xl border-gray-10"
                >
                  +
                </div>
                <div class="text-root-1 text-xs md:text-sm font-normal mr-4">
                  Deposit
                </div>
              </div>
              <div
                @click="proceedToWithdraw"
                class="flex cursor-pointer items-center"
              >
                <div
                  class="text-gray-10 bg-root-1 border h-4 mr-2 pb-0.5 w-4 flex justify-center items-center rounded-3xl border-gray-10"
                >
                  -
                </div>
                <div class="text-root-1 text-xs md:text-sm font-normal">
                  Withdraw
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-6 bg-white dashboard-card">
          <div class="flex ml-8 mt-4 pb-1.5 items-center justify-between">
            <div class="flex font-normal text-root-1 text-sm md:text-base">
              Interest Report
            </div>
            <el-select
              style="width: 150px"
              suffix-icon="el-icon-date"
              class="mr-2"
              v-model="interestRange"
              placeholder="Select range"
              @change="getFilterData"
            >
              <el-option
                suffix-icon="el-icon-date"
                v-for="item in rangeOptions"
                :key="item"
                :label="item"
                :value="item"
              >
              </el-option>
            </el-select>
          </div>
          <apexchart
            v-if="!isLoadingYieldData"
            height="200"
            type="bar"
            :options="chartOptionsInterest"
            :series="seriesInterest"
          ></apexchart>
          <div v-else class="mt-10">
            <spinner
              size="medium"
              :spacing="40"
              message=""
              :line-fg-color="$primaryColor"
            />
          </div>
        </div>
        <div class="col-span-8 rounded-2xl bg-white dashboard-card">
          <div
            class="flex text-root-1 text-sm md:text-base pl-7 py-4 font-normal justify-start"
          >
            Transactions
          </div>

          <div v-if="!isLoadingTransactions">
            <div
              v-if="noTransactions"
              class="flex justify-center text-center mt-8 font-bold text-lg text-black-2"
            >
              <transaction-empty-state use="dashboard">
                <h3 class="text-sm md:text-base font-semibold">
                  No Transactions
                </h3>
                <p class="font-light text-xs md:text-sm my-3 md:mt-5">
                  Your transactions show up here
                </p>
              </transaction-empty-state>
            </div>
            <div
              v-else
              v-for="(transaction, index) in latestTransactions"
              :key="index"
            >
              <div>
                <div
                  @click="showTransaction(transaction)"
                  class="bg-root-2 cursor-pointer mx-7 pl-2 pr-5 py-0.5 mb-2 rounded-lg"
                >
                  <div class="flex w-full items-center justify-between">
                    <div class="flex items-center">
                      <arrow-up
                        class="ml-3"
                        v-if="
                          transaction.type === $transactionType.WITHDRAWAL ||
                          transaction.type === $transactionType.TRANSFER
                        "
                      />
                      <arrow-down class="ml-3" v-else />
                      <div class="lg:ml-4 ml-1 flex flex-col">
                        <div class="items-center flex text-root-2">
                          <div
                            class="capitalize pr-2 font-bold text-sm md:text-base"
                          >
                            {{ transaction.type }}
                          </div>
                          <div
                            v-if="
                              transaction.type === $transactionType.TRANSFER &&
                              rateIncreased(transaction)
                            "
                          >
                            <increase-icon />
                          </div>
                          <div
                            v-if="transaction.status !== $STATUS.COMPLETED"
                            class="ml-2 text-9px capitalize px-2 font-normal border rounded-lg"
                            :class="
                              transaction.status === $STATUS.PENDING
                                ? 'text-yellow-10 border-yellow-10'
                                : transaction.status === $STATUS.FAILED
                                ? 'text-red-1 border-red-1'
                                : transaction.status === $STATUS.CANCELLED
                                ? 'text-red-1 border-red-1'
                                : 'text-yellow-10 border-yellow-10'
                            "
                          >
                            {{ transaction.status.replace(/_/g, ' ') }}
                          </div>
                        </div>
                        <div class="text-xs text-gray-40 font-normal">
                          {{
                            $dayjs(transaction.createdAt).format('DD, MMM YYYY')
                          }}
                        </div>
                      </div>
                    </div>
                    <div
                      class="lg:ml-12 ml-3 text-black-1 mr-3 font-semibold flex flex-col items-end"
                    >
                      <div class="text-root-2 pt-1">
                        {{
                          transaction.type === $transactionType.DEPOSIT
                            ? '+'
                            : '-'
                        }}
                        {{
                          transaction.type === $transactionType.WITHDRAWAL &&
                          transaction.method === $transactionType.ASSET_TRANSFER
                            ? parseMoneyAll(
                                transaction.amount +
                                  (transaction.businessCommission || 0),
                                transaction.currency
                              )
                            : transaction.method ===
                              $transactionType.ASSET_TRANSFER
                            ? parseMoneyAll(
                                transaction.depositedAmount +
                                  (transaction.businessCommission || 0),
                                transaction.depositedCurrency
                              )
                            : transaction.method ===
                              $transactionType.LOCAL_TRANSFER
                            ? parseMoneyAll(
                                transaction.depositedAmount +
                                  (transaction.businessCommission || 0),
                                transaction.depositedCurrency
                              )
                            : parseMoneyAll(
                                (transaction.transferredAmount ||
                                  transaction.amount) +
                                  (transaction.businessCommission || 0),
                                transaction.currency
                              )
                        }}
                      </div>
                      <div
                        v-if="transaction.status === $STATUS.COMPLETED"
                        class="text-xs mb-3 font-normal flex items-center text-green"
                      >
                        {{
                          transaction.type === $transactionType.DEPOSIT
                            ? '+' +
                              parseMoneyAll(
                                transaction.amount +
                                  (transaction.businessCommission || 0),
                                transaction.currency
                              )
                            : '-' +
                              parseMoneyAll(
                                (transaction.transferredAmount ||
                                  transaction.amount) +
                                  (transaction.businessCommission || 0),
                                transaction.currency
                              )
                        }}
                      </div>
                      <div
                        v-else
                        class="text-xs mb-3 font-normal text-gray-40 capitalize flex items-center"
                      >
                        {{ '-' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="mt-10">
            <spinner
              size="medium"
              :spacing="40"
              message=""
              :line-fg-color="$primaryColor"
            />
          </div>
          <div
            v-if="
              latestTransactions &&
              latestTransactions.length > 2 &&
              !isLoadingTransactions
            "
            @click="goToTransactions()"
            class="mx-7 mb-3 mt-3 py-1.5 text-xs cursor-pointer flex text justify-center font-normal items-center rounded-md text-gray-40 button-border bg-root-1"
          >
            See more
          </div>
        </div>
        <div class="flex-col flex col-span-4">
          <div class="w-full p-4 dashboard-card">
            <div
              class="flex text-root-1 text-base font-semibold items-center justify-start"
            >
              <div class="p-1 text-root-1 py-2 mr-2 text-sm md:text-base">
                <interest-icon />
              </div>
              Total Interest Earned
            </div>
            <div
              class="text-lg md:text-2xl text-root-1 flex pl-8 pt-3 justify-start font-semibold"
            >
              {{ parseMoney(totalYield) }}
            </div>
          </div>
          <div class="w-full dashboard-card p-4 mt-5">
            <div
              class="flex text-root-1 text-base font-semibold items-center justify-start"
            >
              <div class="p-1 text-root-1 py-2 mr-2 text-sm md:text-base">
                <interest-apy />
              </div>
              APY changes
            </div>

            <div v-if="!isLoadingYieldData" id="chart">
              <apexchart
                height="80"
                type="area"
                :options="chartOptions"
                :series="series"
              ></apexchart>
            </div>
            <div v-else class="mt-10">
              <spinner
                size="medium"
                :spacing="40"
                message=""
                :line-fg-color="$primaryColor"
              />
            </div>
            <div class="flex justify-between">
              <div class=""></div>
              <div
                @click="openInterestCalculator"
                class="rounded-full button-border mb-3 mt-3 py-2 px-5 text-xs cursor-pointer flex text justify-center font-normal items-center text-gray-40 bg-root-1"
              >
                Interest Calculator >
              </div>
            </div>
          </div>
        </div>
      </div>
      <deposit-pending-dialog
        v-if="showDepositPendingDialog"
        :showDialog="showDepositPendingDialog"
        @closeDialog="closePendingDepositDialog"
        @proceedToDeposit="proceedToDeposit"
        @showDepositDetails="showDepositDetails"
        :pendingDepositDetails="pendingDepositDetails"
      />
      <interest-calculator-dialog
        v-if="showInterestCalculatorDialog"
        :showDialog="showInterestCalculatorDialog"
        @closeDialog="closeDialog"
      />
      <transaction-dialog
        v-if="showTransactionDialog"
        :showDialog="showTransactionDialog"
        @closeDialog="closeDialog"
        :rateIncreased="transactionHasRateChange"
        :transaction="currentTransaction"
        @showDepositDetails="showDepositDetails"
        @showDepositType="showDepositType"
      />
    </div>
    <div v-else class="md:mx-14 mx-4">
      <el-skeleton :loading="isLoadingTransactions" class="w-full" animated>
        <template slot="template">
          <div class="grid lg:gap-6 mt-12 gap-y-4 lg:grid-cols-12 grid-cols-1">
            <el-skeleton-item variant="rect" class="col-span-6 h-72" />
            <el-skeleton-item variant="rect" class="col-span-6 h-72" />
            <el-skeleton-item variant="rect" class="col-span-8 h-72" />
            <div class="col-span-4 h-72">
              <div class="flex flex-col">
                <el-skeleton-item variant="rect" class="h-28 mb-4" />
                <el-skeleton-item variant="rect" class="h-40" />
              </div>
            </div>
          </div>
        </template>
      </el-skeleton>
    </div>
  </div>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import eventBus from '@/global/eventBus'
import pendingDeposit from '@/mixins/pendingDeposit.js'
import {
  digitsBeforeDecimal,
  digitsAfterDecimal,
  noAfterDecimal,
  expectedInterestYears,
  expectedInterestSeconds,
  getXDatesFromToday,
  ParseMoneyAll,
  ParseMoney
} from '@/utils/helpers'
import getBalance from '@/mixins/getBalance.js'
import { WITHDRAWAL_ACCESS, DEPOSIT_ACCESS } from '@/utils/constants'
import logout from '@/mixins/logout.js'
import reports from '@/mixins/getDashboardReports.js'
import localDeposit from '@/mixins/localDeposit.js'
import transactions from '@/mixins/transactions.js'

// import ICountUp from 'vue-countup-v2'

export default {
  mixins: [
    pendingDeposit,
    localDeposit,
    getBalance,
    reports,
    logout,
    transactions
  ],
  name: 'Dashboard',
  data () {
    return {
      showDepositPendingDialog: false,
      interestRange: '7days report',
      rangeOptions: ['7days report'],
      showTransactionDialog: false,
      currentTransaction: {},
      showInterestCalculatorDialog: false,
      interestInterval: null,
      updatedBalance: 0,
      withdrawalAccess: WITHDRAWAL_ACCESS,
      transactionHasRateChange: false,
      depositAccess: DEPOSIT_ACCESS,
      countup: {
        delay: 50000,
        options: {
          useEasing: true,
          useGrouping: true,
          separator: ',',
          decimal: '.',
          prefix: '$',
          decimalPlaces: this.newBalance === 0 ? 0 : 3
        },
        expectedInterestOptions: {
          useEasing: true,
          useGrouping: true,
          separator: ',',
          decimal: '.',
          prefix: '$',
          decimalPlaces: this.expectedInterest === 0 ? 0 : 3
        }
      },
      activeNames: [],
      isLoadingYieldData: false,
      yearCount: 1,
      expectedInterest: 0,
      graphView: false,
      isLoading: false,
      isLoadingTransactions: false,
      breakpoints,
      walletAddress: '',
      options: {
        stroke: {
          width: [2]
        },
        chart: {
          id: 'vuechart-example',
          toolbar: {
            show: false
          }
        },
        grid: {
          show: false,
          xaxis: {
            lines: {
              show: true
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          }
        },
        dataLabels: {
          enabled: false,
          position: 'right'
        },
        colors: [this.$primaryColor],
        xaxis: {
          categories: [1, 3, 5, 10],
          floating: true
        },
        markers: {
          size: [4]
        },
        yaxis: [
          {
            seriesName: '',
            opposite: true,
            floating: true,
            axisTicks: {
              show: true
            },
            axisBorder: {
              show: true
            },
            title: {
              text: ''
            }
          }
        ],
        legend: {
          horizontalAlign: 'left',
          offsetX: 40
        }
      },
      series: [
        {
          name: 'APY(%)',
          data: [0, 0, 0, 0, 0, 0, 0]
        }
      ],
      chartOptions: {
        chart: {
          toolbar: {
            show: false
          },
          type: 'area',
          height: 100,
          zoom: {
            enabled: false
          }
        },
        title: {
          text: '',
          align: 'right'
        },
        colors: [this.$primaryColor],
        dataLabels: {
          enabled: false
        },
        stroke: {
          // curve: 'straight',
          width: [2]
        },
        labels: [
          '11/08/2022',
          '11/09/2022',
          '11/10/2022',
          '11/11/2022',
          '11/12/2022',
          '11/13/2022',
          '11/14/2022'
        ],
        xaxis: {
          type: 'datetime',
          show: false,
          lines: {
            show: true
          },
          floating: true
        },
        grid: {
          show: false,
          xaxis: {
            lines: {
              show: false
            }
          },
          colors: [this.$primaryColor],
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        yaxis: {
          opposite: true,
          show: false
        },
        legend: {
          horizontalAlign: 'right'
        }
      },

      // iNTEREST CHART OPTIONS

      chartOptionsInterest: {
        yaxis: {
          opposite: false,
          show: false,
          labels: {
            formatter: function (value) {
              return '$' + value
            }
          }
        },
        chart: {
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        grid: {
          show: false,
          xaxis: {
            lines: {
              show: false
            }
          },
          colors: [process.env.VUE_APP_TENANT_PRIMARY_COLOR],
          yaxis: {
            lines: {
              show: false
            }
          }
        },
        colors: [
          function ({ value, seriesIndex, dataPointIndex, w }) {
            if (dataPointIndex === 6) {
              return process.env.VUE_APP_TENANT_PRIMARY_COLOR
            } else {
              return 'rgba(189, 189, 189, 1)'
            }
          }
        ],
        plotOptions: {
          bar: {
            dataLabels: {
              position: 'bottom',
              enabledOnSeries: 'false'
            },
            borderRadius: 20,
            colors: {
              backgroundBarColors: [],
              backgroundBarOpacity: 0,
              backgroundBarRadius: 0
            }
          }
        },
        dataLabels: {
          style: {
            fontSize: '25px',
            fontWeight: 'normal'
          },
          enabled: false
        },
        xaxis: {
          categories: [],
          type: 'datetime',
          labels: {
            format: 'ddd'
          }
        },
        tooltip: {}
      },

      seriesInterest: [
        {
          name: 'Interest',
          data: [0, 0, 0, 0, 0, 0, 0]
        }
      ]
    }
  },

  methods: {
    getInterestDates (number) {
      const dates = getXDatesFromToday(number)
      this.chartOptionsInterest = {
        ...this.chartOptionsInterest,
        ...{
          xaxis: {
            categories: dates,
            type: 'datetime',
            labels: {
              format: 'ddd'
            }
          }
        }
      }
      let amountList = []
      if (this.getAllInterestAmount.length < 7) {
        amountList = this.getUniformDateRates(this.getAllInterestAmount)
      } else {
        amountList = this.getAllInterestAmount
      }
      if (this.getAllInterestAmount.length > 0) {
        this.seriesInterest = [
          {
            data: amountList
          }
        ]
      }
    },
    getApyDates () {
      const dates = getXDatesFromToday(7)
      this.chartOptions = {
        ...this.chartOptions,
        ...{
          labels: dates
        }
      }
      let rates = []
      if (this.getApyRates.length < 7) {
        rates = this.getUniformDateRates(this.getApyRates)
      } else {
        rates = this.getApyRates
      }
      this.series = [
        {
          data: rates
        }
      ]
    },
    getUniformDateRates (arr) {
      const newArr = Array.apply(null, new Array(7)).map(
        Number.prototype.valueOf,
        0
      )
      let pointer = newArr.length - 1
      for (let i = arr.length - 1; i > -1; i -= 1) {
        newArr[pointer] = arr[i]
        pointer -= 1
      }
      return newArr
    },
    showDepositType () {
      eventBus.$emit('showDepositTypeEvent')
    },
    showDepositDetails (transactionMethod) {
      if (transactionMethod === this.$transactionType.LOCAL_TRANSFER) {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'localAccountInformation'
        )
      } else if (transactionMethod === this.$transactionType.ASSET_TRANSFER) {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'assetAccountInformation'
        )
      } else {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'accountInformation'
        )
      }
      eventBus.$emit('depositDetailsEvent')
    },
    showTransaction (transaction) {
      this.$store.dispatch('transactions/setCurrentTransaction', transaction)
      this.currentTransaction = transaction
      this.transactionHasRateChange = this.rateIncreased(transaction)
      this.showTransactionDialog = true
    },
    openInterestCalculator () {
      this.showInterestCalculatorDialog = true
    },
    goToTransactions () {
      this.$router.push({ name: 'transactions' })
    },
    async getTransactions () {
      const vm = this
      const payload = {
        page: 1,
        size: 10,
        status: '',
        type: '',
        from: '',
        to: ''
      }
      await this.$store
        .dispatch('transactions/getTransactions', payload)
        .then(() => {
          this.isLoadingTransactions = false
        })
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
          clearInterval(this.detailsInterval)
        })
    },
    async getYields () {
      const payload = {}
      payload.to = this.$dayjs().format('DD/MM/YYYY')
      payload.from = this.$dayjs().add(-7, 'days').format('DD/MM/YYYY')
      const vm = this
      await this.$store
        .dispatch('report/getYields', payload)
        .then(() => {
          this.isLoadingYieldData = false
        })
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
        })
        .finally(() => {})
    },
    digitsBeforeDecimal (val) {
      return digitsBeforeDecimal(val)
    },
    digitsAfterDecimal (val) {
      return digitsAfterDecimal(val)
    },
    noAfterDecimal (val) {
      return noAfterDecimal(val)
    },
    getExpectedInterest (yearCount) {
      if (yearCount === 1) {
        this.yearCount = 1
        this.expectedInterest = this.series[0].data[0] || 0
      } else if (yearCount === 3) {
        this.yearCount = 3
        this.expectedInterest = this.series[0].data[1] || 0
      } else if (yearCount === 10) {
        this.yearCount = 10
        this.expectedInterest = this.series[0].data[2] || 0
      }
    },
    proceedToWithdraw () {
      if (!this.withdrawalAccess.includes(this.user.kind)) {
        this.$message({
          showClose: true,
          message: 'No withdrawal access',
          type: 'info'
        })
        return
      }
      this.$router.push(this.$transactionType.WITHDRAWAL)
    },
    goToDeposit () {
      if (this.hasPendingDeposit && this.pendingDepositDetails) {
        this.showDepositPendingDialog = true
      } else {
        this.proceedToDeposit()
      }
    },
    proceedToDeposit () {
      eventBus.$emit('depositAction')
    },
    getFilterData () {
      // this.isLoadingYieldData = true
      // await this.getYields()
    },
    closeDialog () {
      this.showInterestCalculatorDialog = false
      this.showTransactionDialog = false
    },
    closeDepositAmountDialog () {
      this.showDepositAmountDialog = false
    },
    closeWalletDialog () {
      this.showWalletDialog = false
    },
    closeEditPendingDepositDialog () {
      this.showEditPendingDepositDialog = false
    },
    closeDepositTypeDialog () {
      this.showDepositTypeDialog = false
    },
    closePendingDepositDialog () {
      this.showDepositPendingDialog = false
    },
    async getDetails () {
      await this.getDashboardReports()
      await this.getTransactions()
      await this.getYields()
    },
    getAnalyticalData () {
      const newArr = [
        expectedInterestYears(this.apyValue, 1, this.totalBalance),
        expectedInterestYears(this.apyValue, 3, this.totalBalance),
        expectedInterestYears(this.apyValue, 10, this.totalBalance)
      ]
      this.series[0].data = newArr
      this.getExpectedInterest(1)
    },
    getNewBalance () {
      this.updatedBalance = expectedInterestSeconds(
        this.apyValue,
        this.totalBalance
      )
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    parseMoney (amount) {
      return ParseMoney(amount)
    },
    pendingAssetDeposits () {
      const payload = {
        page: 1,
        size: 10,
        status: $STATUS.PENDING, // should be updated to pending later
        type: 'asset_deposit',
        from: '',
        to: ''
      }
      this.getDeposits(payload)
    }
  },
  components: {
    ArrowUp: () => import('@/assets/icons/ArrowUp.vue'),
    ArrowDown: () => import('@/assets/icons/ArrowDown.vue'),
    VerticalDotsIcon: () => import('@/assets/icons/VerticalDots.vue'),
    InterestUp: () => import('@/assets/icons/InterestUp.vue'),
    InterestIcon: () => import('@/assets/icons/InterestIcon.vue'),
    InterestApy: () => import('@/assets/icons/Interest.vue'),
    DepositPendingDialog: () =>
      import('@/components/deposit/DepositPendingDialog.vue'),
    IncreaseIcon: () => import('@/assets/icons/IncreaseIcon.vue'),
    InterestCalculatorDialog: () =>
      import('@/components/app/InterestCalculatorDialog.vue'),
    TransactionDialog: () => import('@/components/app/TransactionDialog.vue'),
    TransactionEmptyState: () =>
      import('@/components/empty-states/TransactionEmptyState.vue')
  },
  computed: {
    newBalance () {
      return this.updatedBalance
    },
    user () {
      return this.$store.state.auth.user
    },
    isbusinessAccountActive () {
      return this.$store.state.auth.business.accountActive
    },
    getAllYields () {
      return this.$store.state.report.yields || []
    },
    getApyRates () {
      if (this.getAllYields && this.getAllYields.length > 0) {
        const apyRates = this.getAllYields.map((rate) => rate.apyRate)
        return apyRates.reverse()
      } else {
        return []
      }
    },
    getAllInterestAmount () {
      if (this.getAllYields && this.getAllYields.length > 0) {
        const allAmount = this.getAllYields.map((amount) =>
          amount.amount.amount.toFixed(3)
        )
        return allAmount.reverse()
      } else {
        return []
      }
    },
    agreementContract () {
      return this.$store.state.transactions.depositContract
    },
    latestTransactions () {
      if (
        !this.$store.state.transactions.transactions ||
        !this.$store.state.transactions.transactions.items
      ) {
        return []
      }
      return this.$store.state.transactions.transactions.items.slice(0, 3)
    },
    noTransactions () {
      return !this.latestTransactions.length > 0
    }
  },
  async mounted () {
    this.isLoadingYieldData = true
    this.isLoadingTransactions = true
    await this.getTransferDetails()
    await this.getDetails()
    await this.getInterestDates(7)
    await this.getApyDates()
    this.detailsInterval = setInterval(() => this.getDetails(), 30000)
    this.getAnalyticalData()

    // this.getNewBalance()
    // this.interestInterval = setInterval(() => this.getNewBalance(), 1000)
  },
  beforeDestroy () {
    clearInterval(this.detailsInterval)
    clearInterval(this.interestInterval)
  }
}
</script>

<style scoped scss>
.text-root-2 {
  color: var(--text-2);
}
.text-root-1 {
  color: var(--text-1);
}
.text-bg-root-1 {
  color: var(--background);
}
.rounded-40px {
  border-radius: 40px;
}
.rounded-30px {
  border-radius: 30px;
}
.bg-root {
  background: var(--background);
}
.bg-root-1 {
  background: var(--background-1);
}
.bg-root-2 {
  background: var(--background-2);
}
.dashboard-card {
  /* width: 525px;
  height: 283px; */
  /* left: 19px;
  top: 2px; */

  background: var(--background);
  /* Shadow/Theme/60 */

  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 4px 8px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}
.card-shadow {
  box-shadow: 0 2px 12px 0 rgb(0 0 0 / 10%);
}
.border-bottom {
  border-bottom: 1.5px solid #dddddd;
}
.button-border {
  border: 0.5px solid var(--text-3);
}
>>> .el-select .el-input .el-select__caret {
  color: var(--color-primary);
}

/* >>> .el-collapse-item__header {
  height: 58px;
} */
/* @media screen and (min-width: 1024px) {
>>> .el-collapse-item__header {
  height: 58px !important;
}
} */
>>> .el-input__inner {
  border: none;
  background: var(--background);
  color: #9e9e9e;
}
</style>
